import { ITkMarkerRef } from '@ws/shared/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getProjectRoute } from '../../utils/routes';

import { revealMarker, waitForMarker } from './revealMarker';

/** Сколько ждать появления метки после перехода, прежде чем сдаться. */
const WAIT_TIMEOUT_MS = 5000;

/**
 * Переход к метке из панели.
 *
 * Сначала смотрим, нет ли заметки уже на экране: при открытой «Ленте» коллекции
 * нужная заметка отрисована, и переход по адресу выдернул бы автора из ленты в
 * одиночную заметку — то есть сломал бы то, что он читал.
 *
 * Если заметки на экране нет — идём каноническим путём, через адрес, а затем
 * дожидаемся появления самой метки. Ожидание отменяется по новому клику и по
 * размонтированию: иначе запоздавший наблюдатель уведёт экран уже после того,
 * как автор выбрал другую метку.
 */
export function useJumpToMarker() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      cancelRef.current?.();
      cancelRef.current = null;
    },
    [],
  );

  return useCallback(
    (marker: ITkMarkerRef) => {
      cancelRef.current?.();
      cancelRef.current = null;

      const target = { id: marker.id, ordinal: marker.ordinal };

      if (revealMarker(marker.noteId, target)) {
        return;
      }

      if (projectId) {
        navigate(getProjectRoute(projectId, marker.noteId));
      }

      cancelRef.current = waitForMarker(marker.noteId, target, WAIT_TIMEOUT_MS);
    },
    [navigate, projectId],
  );
}
